/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { ListDeploymentsWrapper } from '../models/list-deployments-wrapper';
import { DeploymentLog } from '../models/deployment-log';
import { DeploymentRequest } from '../models/deployment-request';
import { DeploymentOverview } from '../models/deployment-overview';
import { HotfixDeploymentRecipe } from '../models/hotfix-deployment-recipe';
import { PageDeploymentLog } from '../models/page-deployment-log';
import { ChangeRepresentation } from '../models/change-representation';
import { DeploymentStatsDto } from '../models/deployment-stats-dto';
import { TokenPaginatedResponse } from '../models/token-paginated-response';
import { StreamingResponseBody } from '../models/streaming-response-body';
import { ReleaseChanges } from '../models/release-changes';

/**
 * Ui Deployment Controller
 */
@Injectable({
  providedIn: 'root',
})
class UiDeploymentControllerService extends __BaseService {
  static readonly getDeploymentsUsingGET1Path =
    '/cc-ui/v1/clusters/{clusterId}/deployments';
  static readonly createDeploymentUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments';
  static readonly cleanS3SourcesUsingDELETEPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/clean-s3-sources';
  static readonly destroyClusterUsingDELETEPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/destroy';
  static readonly getLatestReleaseUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/latest-successful-release';
  static readonly getLatestReleaseByApplicationUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/latest-successful-release/{application}';
  static readonly launchClusterUsingPUTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/launch';
  static readonly triggerMaintenanceReleaseUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/maintenance';
  static readonly getDeploymentsOverviewUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/overview';
  static readonly abortAutomationSuiteUsingDELETE1Path =
    '/cc-ui/v1/clusters/{clusterId}/deployments/qa/{executionId}/abortSuite';
  static readonly runHotfixDeploymentRecipeUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/recipes/deployment/hotfix';
  static readonly releaseUsingPUTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/release';
  static readonly releaseV2UsingPUTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/releaseV2/{releaseType}';
  static readonly searchDeploymentsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/search';
  static readonly simulateUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/simulate';
  static readonly getClusterStateUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/state';
  static readonly getDeploymentStatsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/stats';
  static readonly triggerTerraformExportUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/terraform-export';
  static readonly stateUnlockUsingPUTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/unlock';
  static readonly getDeploymentUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}';
  static readonly approveReleaseUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/approveRelease';
  static readonly downloadTerraformExportUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/download-terraform-export';
  static readonly getDeploymentLogsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/logs';
  static readonly streamDeploymentLogsUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/logs/stream';
  static readonly rejectReleaseUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/rejectRelease';
  static readonly getReleaseChangesUsingGETPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/release-changes';
  static readonly signOffDeploymentUsingPUTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/signoff';
  static readonly triggerRollbackPlanReleaseUsingPOSTPath =
    '/cc-ui/v1/clusters/{clusterId}/deployments/{deploymentId}/{resourceType}/{resourceName}/rollback-plan';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * getDeployments
   * @param clusterId clusterId
   * @return OK
   */
  getDeploymentsUsingGET1Response(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<ListDeploymentsWrapper>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ListDeploymentsWrapper>;
      }),
    );
  }
  /**
   * getDeployments
   * @param clusterId clusterId
   * @return OK
   */
  getDeploymentsUsingGET1(
    clusterId: string,
  ): __Observable<ListDeploymentsWrapper> {
    return this.getDeploymentsUsingGET1Response(clusterId).pipe(
      __map((_r) => _r.body as ListDeploymentsWrapper),
    );
  }

  /**
   * createDeployment
   * @param params The `UiDeploymentControllerService.CreateDeploymentUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentRequest`: deploymentRequest
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  createDeploymentUsingPOSTResponse(
    params: UiDeploymentControllerService.CreateDeploymentUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.deploymentRequest;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * createDeployment
   * @param params The `UiDeploymentControllerService.CreateDeploymentUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentRequest`: deploymentRequest
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  createDeploymentUsingPOST(
    params: UiDeploymentControllerService.CreateDeploymentUsingPOSTParams,
  ): __Observable<DeploymentLog> {
    return this.createDeploymentUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * cleanS3Sources
   * @param clusterId clusterId
   */
  cleanS3SourcesUsingDELETEResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/clean-s3-sources`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * cleanS3Sources
   * @param clusterId clusterId
   */
  cleanS3SourcesUsingDELETE(clusterId: string): __Observable<null> {
    return this.cleanS3SourcesUsingDELETEResponse(clusterId).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * destroyCluster
   * @param clusterId clusterId
   * @return OK
   */
  destroyClusterUsingDELETEResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/destroy`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * destroyCluster
   * @param clusterId clusterId
   * @return OK
   */
  destroyClusterUsingDELETE(clusterId: string): __Observable<DeploymentLog> {
    return this.destroyClusterUsingDELETEResponse(clusterId).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * getLatestRelease
   * @param clusterId clusterId
   * @return OK
   */
  getLatestReleaseUsingGETResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/latest-successful-release`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * getLatestRelease
   * @param clusterId clusterId
   * @return OK
   */
  getLatestReleaseUsingGET(clusterId: string): __Observable<DeploymentLog> {
    return this.getLatestReleaseUsingGETResponse(clusterId).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * getLatestReleaseByApplication
   * @param params The `UiDeploymentControllerService.GetLatestReleaseByApplicationUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `application`: application
   *
   * @return OK
   */
  getLatestReleaseByApplicationUsingGETResponse(
    params: UiDeploymentControllerService.GetLatestReleaseByApplicationUsingGETParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/latest-successful-release/${encodeURIComponent(
          String(params.application),
        )}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * getLatestReleaseByApplication
   * @param params The `UiDeploymentControllerService.GetLatestReleaseByApplicationUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `application`: application
   *
   * @return OK
   */
  getLatestReleaseByApplicationUsingGET(
    params: UiDeploymentControllerService.GetLatestReleaseByApplicationUsingGETParams,
  ): __Observable<DeploymentLog> {
    return this.getLatestReleaseByApplicationUsingGETResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * launchCluster
   * @param clusterId clusterId
   * @return OK
   */
  launchClusterUsingPUTResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/launch`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * launchCluster
   * @param clusterId clusterId
   * @return OK
   */
  launchClusterUsingPUT(clusterId: string): __Observable<DeploymentLog> {
    return this.launchClusterUsingPUTResponse(clusterId).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * triggerMaintenanceRelease
   * @param clusterId clusterId
   * @return OK
   */
  triggerMaintenanceReleaseUsingPOSTResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/maintenance`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * triggerMaintenanceRelease
   * @param clusterId clusterId
   * @return OK
   */
  triggerMaintenanceReleaseUsingPOST(
    clusterId: string,
  ): __Observable<DeploymentLog> {
    return this.triggerMaintenanceReleaseUsingPOSTResponse(clusterId).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * getDeploymentsOverview
   * @param clusterId clusterId
   * @return OK
   */
  getDeploymentsOverviewUsingGETResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<DeploymentOverview>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/overview`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentOverview>;
      }),
    );
  }
  /**
   * getDeploymentsOverview
   * @param clusterId clusterId
   * @return OK
   */
  getDeploymentsOverviewUsingGET(
    clusterId: string,
  ): __Observable<DeploymentOverview> {
    return this.getDeploymentsOverviewUsingGETResponse(clusterId).pipe(
      __map((_r) => _r.body as DeploymentOverview),
    );
  }

  /**
   * abortAutomationSuite
   * @param params The `UiDeploymentControllerService.AbortAutomationSuiteUsingDELETE1Params` containing the following parameters:
   *
   * - `executionId`: executionId
   *
   * - `clusterId`: clusterId
   */
  abortAutomationSuiteUsingDELETE1Response(
    params: UiDeploymentControllerService.AbortAutomationSuiteUsingDELETE1Params,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/qa/${encodeURIComponent(
          String(params.executionId),
        )}/abortSuite`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * abortAutomationSuite
   * @param params The `UiDeploymentControllerService.AbortAutomationSuiteUsingDELETE1Params` containing the following parameters:
   *
   * - `executionId`: executionId
   *
   * - `clusterId`: clusterId
   */
  abortAutomationSuiteUsingDELETE1(
    params: UiDeploymentControllerService.AbortAutomationSuiteUsingDELETE1Params,
  ): __Observable<null> {
    return this.abortAutomationSuiteUsingDELETE1Response(params).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * runHotfixDeploymentRecipe
   * @param params The `UiDeploymentControllerService.RunHotfixDeploymentRecipeUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentRecipe`: deploymentRecipe
   *
   * - `clusterId`: clusterId
   *
   * - `withRefresh`: withRefresh
   *
   * - `isPlan`: isPlan
   *
   * - `forceRelease`: forceRelease
   *
   * - `comment`: comment
   *
   * - `canQueue`: canQueue
   *
   * - `allowDestroy`: allowDestroy
   *
   * @return OK
   */
  runHotfixDeploymentRecipeUsingPOSTResponse(
    params: UiDeploymentControllerService.RunHotfixDeploymentRecipeUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.deploymentRecipe;

    if (params.withRefresh != null)
      __params = __params.set('withRefresh', params.withRefresh.toString());
    if (params.isPlan != null)
      __params = __params.set('isPlan', params.isPlan.toString());
    if (params.forceRelease != null)
      __params = __params.set('forceRelease', params.forceRelease.toString());
    if (params.comment != null)
      __params = __params.set('comment', params.comment.toString());
    if (params.canQueue != null)
      __params = __params.set('canQueue', params.canQueue.toString());
    if (params.allowDestroy != null)
      __params = __params.set('allowDestroy', params.allowDestroy.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/recipes/deployment/hotfix`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * runHotfixDeploymentRecipe
   * @param params The `UiDeploymentControllerService.RunHotfixDeploymentRecipeUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentRecipe`: deploymentRecipe
   *
   * - `clusterId`: clusterId
   *
   * - `withRefresh`: withRefresh
   *
   * - `isPlan`: isPlan
   *
   * - `forceRelease`: forceRelease
   *
   * - `comment`: comment
   *
   * - `canQueue`: canQueue
   *
   * - `allowDestroy`: allowDestroy
   *
   * @return OK
   */
  runHotfixDeploymentRecipeUsingPOST(
    params: UiDeploymentControllerService.RunHotfixDeploymentRecipeUsingPOSTParams,
  ): __Observable<DeploymentLog> {
    return this.runHotfixDeploymentRecipeUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * release
   * @param params The `UiDeploymentControllerService.ReleaseUsingPUTParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `withRefresh`: withRefresh
   *
   * - `forceRelease`: forceRelease
   *
   * - `comment`: comment
   *
   * - `canQueue`: canQueue
   *
   * - `allowDestroy`: allowDestroy
   *
   * @return OK
   */
  releaseUsingPUTResponse(
    params: UiDeploymentControllerService.ReleaseUsingPUTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.withRefresh != null)
      __params = __params.set('withRefresh', params.withRefresh.toString());
    if (params.forceRelease != null)
      __params = __params.set('forceRelease', params.forceRelease.toString());
    if (params.comment != null)
      __params = __params.set('comment', params.comment.toString());
    if (params.canQueue != null)
      __params = __params.set('canQueue', params.canQueue.toString());
    if (params.allowDestroy != null)
      __params = __params.set('allowDestroy', params.allowDestroy.toString());
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/release`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * release
   * @param params The `UiDeploymentControllerService.ReleaseUsingPUTParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `withRefresh`: withRefresh
   *
   * - `forceRelease`: forceRelease
   *
   * - `comment`: comment
   *
   * - `canQueue`: canQueue
   *
   * - `allowDestroy`: allowDestroy
   *
   * @return OK
   */
  releaseUsingPUT(
    params: UiDeploymentControllerService.ReleaseUsingPUTParams,
  ): __Observable<DeploymentLog> {
    return this.releaseUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * releaseV2
   * @param params The `UiDeploymentControllerService.ReleaseV2UsingPUTParams` containing the following parameters:
   *
   * - `releaseType`: releaseType
   *
   * - `clusterId`: clusterId
   *
   * - `withRefresh`: withRefresh
   *
   * - `forceRelease`: forceRelease
   *
   * - `comment`: comment
   *
   * - `canQueue`: canQueue
   *
   * - `allowDestroy`: allowDestroy
   *
   * @return OK
   */
  releaseV2UsingPUTResponse(
    params: UiDeploymentControllerService.ReleaseV2UsingPUTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.withRefresh != null)
      __params = __params.set('withRefresh', params.withRefresh.toString());
    if (params.forceRelease != null)
      __params = __params.set('forceRelease', params.forceRelease.toString());
    if (params.comment != null)
      __params = __params.set('comment', params.comment.toString());
    if (params.canQueue != null)
      __params = __params.set('canQueue', params.canQueue.toString());
    if (params.allowDestroy != null)
      __params = __params.set('allowDestroy', params.allowDestroy.toString());
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/releaseV2/${encodeURIComponent(
          String(params.releaseType),
        )}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * releaseV2
   * @param params The `UiDeploymentControllerService.ReleaseV2UsingPUTParams` containing the following parameters:
   *
   * - `releaseType`: releaseType
   *
   * - `clusterId`: clusterId
   *
   * - `withRefresh`: withRefresh
   *
   * - `forceRelease`: forceRelease
   *
   * - `comment`: comment
   *
   * - `canQueue`: canQueue
   *
   * - `allowDestroy`: allowDestroy
   *
   * @return OK
   */
  releaseV2UsingPUT(
    params: UiDeploymentControllerService.ReleaseV2UsingPUTParams,
  ): __Observable<DeploymentLog> {
    return this.releaseV2UsingPUTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * searchDeployments
   * @param params The `UiDeploymentControllerService.SearchDeploymentsUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `triggeredBy`: triggeredBy
   *
   * - `tfVersion`: tfVersion
   *
   * - `status`: status
   *
   * - `start`: start
   *
   * - `signedOff`: signedOff
   *
   * - `releaseType`: releaseType
   *
   * - `pageSize`: pageSize
   *
   * - `pageNumber`: pageNumber
   *
   * - `excludeStatus`: excludeStatus
   *
   * - `end`: end
   *
   * @return OK
   */
  searchDeploymentsUsingGETResponse(
    params: UiDeploymentControllerService.SearchDeploymentsUsingGETParams,
  ): __Observable<__StrictHttpResponse<PageDeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.triggeredBy != null)
      __params = __params.set('triggeredBy', params.triggeredBy.toString());
    if (params.tfVersion != null)
      __params = __params.set('tfVersion', params.tfVersion.toString());
    if (params.status != null)
      __params = __params.set('status', params.status.toString());
    if (params.start != null)
      __params = __params.set('start', params.start.toString());
    if (params.signedOff != null)
      __params = __params.set('signedOff', params.signedOff.toString());
    if (params.releaseType != null)
      __params = __params.set('releaseType', params.releaseType.toString());
    if (params.pageSize != null)
      __params = __params.set('pageSize', params.pageSize.toString());
    if (params.pageNumber != null)
      __params = __params.set('pageNumber', params.pageNumber.toString());
    (params.excludeStatus || []).forEach((val) => {
      if (val != null)
        __params = __params.append('excludeStatus', val.toString());
    });
    if (params.end != null)
      __params = __params.set('end', params.end.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<PageDeploymentLog>;
      }),
    );
  }
  /**
   * searchDeployments
   * @param params The `UiDeploymentControllerService.SearchDeploymentsUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `triggeredBy`: triggeredBy
   *
   * - `tfVersion`: tfVersion
   *
   * - `status`: status
   *
   * - `start`: start
   *
   * - `signedOff`: signedOff
   *
   * - `releaseType`: releaseType
   *
   * - `pageSize`: pageSize
   *
   * - `pageNumber`: pageNumber
   *
   * - `excludeStatus`: excludeStatus
   *
   * - `end`: end
   *
   * @return OK
   */
  searchDeploymentsUsingGET(
    params: UiDeploymentControllerService.SearchDeploymentsUsingGETParams,
  ): __Observable<PageDeploymentLog> {
    return this.searchDeploymentsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as PageDeploymentLog),
    );
  }

  /**
   * simulate
   * @param clusterId clusterId
   * @return OK
   */
  simulateUsingGETResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<Array<ChangeRepresentation>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/simulate`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<ChangeRepresentation>>;
      }),
    );
  }
  /**
   * simulate
   * @param clusterId clusterId
   * @return OK
   */
  simulateUsingGET(
    clusterId: string,
  ): __Observable<Array<ChangeRepresentation>> {
    return this.simulateUsingGETResponse(clusterId).pipe(
      __map((_r) => _r.body as Array<ChangeRepresentation>),
    );
  }

  /**
   * getClusterState
   * @param clusterId clusterId
   * @return OK
   */
  getClusterStateUsingGETResponse(
    clusterId: string,
  ): __Observable<
    __StrictHttpResponse<
      | 'STOPPED'
      | 'RUNNING'
      | 'LAUNCHING'
      | 'DESTROYING'
      | 'LAUNCH_FAILED'
      | 'DESTROY_FAILED'
      | 'UNKNOWN'
      | 'SCALE_DOWN'
      | 'SCALING_DOWN'
      | 'SCALE_DOWN_FAILED'
      | 'SCALING_UP'
      | 'SCALE_UP_FAILED'
    >
  > {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/state`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<
          | 'STOPPED'
          | 'RUNNING'
          | 'LAUNCHING'
          | 'DESTROYING'
          | 'LAUNCH_FAILED'
          | 'DESTROY_FAILED'
          | 'UNKNOWN'
          | 'SCALE_DOWN'
          | 'SCALING_DOWN'
          | 'SCALE_DOWN_FAILED'
          | 'SCALING_UP'
          | 'SCALE_UP_FAILED'
        >;
      }),
    );
  }
  /**
   * getClusterState
   * @param clusterId clusterId
   * @return OK
   */
  getClusterStateUsingGET(
    clusterId: string,
  ): __Observable<
    | 'STOPPED'
    | 'RUNNING'
    | 'LAUNCHING'
    | 'DESTROYING'
    | 'LAUNCH_FAILED'
    | 'DESTROY_FAILED'
    | 'UNKNOWN'
    | 'SCALE_DOWN'
    | 'SCALING_DOWN'
    | 'SCALE_DOWN_FAILED'
    | 'SCALING_UP'
    | 'SCALE_UP_FAILED'
  > {
    return this.getClusterStateUsingGETResponse(clusterId).pipe(
      __map(
        (_r) =>
          _r.body as
            | 'STOPPED'
            | 'RUNNING'
            | 'LAUNCHING'
            | 'DESTROYING'
            | 'LAUNCH_FAILED'
            | 'DESTROY_FAILED'
            | 'UNKNOWN'
            | 'SCALE_DOWN'
            | 'SCALING_DOWN'
            | 'SCALE_DOWN_FAILED'
            | 'SCALING_UP'
            | 'SCALE_UP_FAILED',
      ),
    );
  }

  /**
   * getDeploymentStats
   * @param params The `UiDeploymentControllerService.GetDeploymentStatsUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `days`: days
   *
   * @return OK
   */
  getDeploymentStatsUsingGETResponse(
    params: UiDeploymentControllerService.GetDeploymentStatsUsingGETParams,
  ): __Observable<__StrictHttpResponse<DeploymentStatsDto>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.days != null)
      __params = __params.set('days', params.days.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/stats`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentStatsDto>;
      }),
    );
  }
  /**
   * getDeploymentStats
   * @param params The `UiDeploymentControllerService.GetDeploymentStatsUsingGETParams` containing the following parameters:
   *
   * - `clusterId`: clusterId
   *
   * - `days`: days
   *
   * @return OK
   */
  getDeploymentStatsUsingGET(
    params: UiDeploymentControllerService.GetDeploymentStatsUsingGETParams,
  ): __Observable<DeploymentStatsDto> {
    return this.getDeploymentStatsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as DeploymentStatsDto),
    );
  }

  /**
   * triggerTerraformExport
   * @param clusterId clusterId
   * @return OK
   */
  triggerTerraformExportUsingPOSTResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(clusterId),
        )}/deployments/terraform-export`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * triggerTerraformExport
   * @param clusterId clusterId
   * @return OK
   */
  triggerTerraformExportUsingPOST(
    clusterId: string,
  ): __Observable<DeploymentLog> {
    return this.triggerTerraformExportUsingPOSTResponse(clusterId).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * stateUnlock
   * @param params The `UiDeploymentControllerService.StateUnlockUsingPUTParams` containing the following parameters:
   *
   * - `lockId`: lockId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  stateUnlockUsingPUTResponse(
    params: UiDeploymentControllerService.StateUnlockUsingPUTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.lockId != null)
      __params = __params.set('lockId', params.lockId.toString());

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/unlock`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * stateUnlock
   * @param params The `UiDeploymentControllerService.StateUnlockUsingPUTParams` containing the following parameters:
   *
   * - `lockId`: lockId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  stateUnlockUsingPUT(
    params: UiDeploymentControllerService.StateUnlockUsingPUTParams,
  ): __Observable<DeploymentLog> {
    return this.stateUnlockUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * getDeployment
   * @param params The `UiDeploymentControllerService.GetDeploymentUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getDeploymentUsingGETResponse(
    params: UiDeploymentControllerService.GetDeploymentUsingGETParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(String(params.deploymentId))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * getDeployment
   * @param params The `UiDeploymentControllerService.GetDeploymentUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getDeploymentUsingGET(
    params: UiDeploymentControllerService.GetDeploymentUsingGETParams,
  ): __Observable<DeploymentLog> {
    return this.getDeploymentUsingGETResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * approveRelease
   * @param params The `UiDeploymentControllerService.ApproveReleaseUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  approveReleaseUsingPOSTResponse(
    params: UiDeploymentControllerService.ApproveReleaseUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/approveRelease`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * approveRelease
   * @param params The `UiDeploymentControllerService.ApproveReleaseUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  approveReleaseUsingPOST(
    params: UiDeploymentControllerService.ApproveReleaseUsingPOSTParams,
  ): __Observable<DeploymentLog> {
    return this.approveReleaseUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * downloadTerraformExport
   * @param params The `UiDeploymentControllerService.DownloadTerraformExportUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   */
  downloadTerraformExportUsingGETResponse(
    params: UiDeploymentControllerService.DownloadTerraformExportUsingGETParams,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/download-terraform-export`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * downloadTerraformExport
   * @param params The `UiDeploymentControllerService.DownloadTerraformExportUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   */
  downloadTerraformExportUsingGET(
    params: UiDeploymentControllerService.DownloadTerraformExportUsingGETParams,
  ): __Observable<null> {
    return this.downloadTerraformExportUsingGETResponse(params).pipe(
      __map((_r) => _r.body as null),
    );
  }

  /**
   * getDeploymentLogs
   * @param params The `UiDeploymentControllerService.GetDeploymentLogsUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * - `nextToken`: nextToken
   *
   * @return OK
   */
  getDeploymentLogsUsingGETResponse(
    params: UiDeploymentControllerService.GetDeploymentLogsUsingGETParams,
  ): __Observable<__StrictHttpResponse<TokenPaginatedResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.nextToken != null)
      __params = __params.set('nextToken', params.nextToken.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(String(params.deploymentId))}/logs`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TokenPaginatedResponse>;
      }),
    );
  }
  /**
   * getDeploymentLogs
   * @param params The `UiDeploymentControllerService.GetDeploymentLogsUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * - `nextToken`: nextToken
   *
   * @return OK
   */
  getDeploymentLogsUsingGET(
    params: UiDeploymentControllerService.GetDeploymentLogsUsingGETParams,
  ): __Observable<TokenPaginatedResponse> {
    return this.getDeploymentLogsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as TokenPaginatedResponse),
    );
  }

  /**
   * streamDeploymentLogs
   * @param params The `UiDeploymentControllerService.StreamDeploymentLogsUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  streamDeploymentLogsUsingGETResponse(
    params: UiDeploymentControllerService.StreamDeploymentLogsUsingGETParams,
  ): __Observable<__StrictHttpResponse<StreamingResponseBody>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/logs/stream`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<StreamingResponseBody>;
      }),
    );
  }
  /**
   * streamDeploymentLogs
   * @param params The `UiDeploymentControllerService.StreamDeploymentLogsUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  streamDeploymentLogsUsingGET(
    params: UiDeploymentControllerService.StreamDeploymentLogsUsingGETParams,
  ): __Observable<StreamingResponseBody> {
    return this.streamDeploymentLogsUsingGETResponse(params).pipe(
      __map((_r) => _r.body as StreamingResponseBody),
    );
  }

  /**
   * rejectRelease
   * @param params The `UiDeploymentControllerService.RejectReleaseUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  rejectReleaseUsingPOSTResponse(
    params: UiDeploymentControllerService.RejectReleaseUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/rejectRelease`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * rejectRelease
   * @param params The `UiDeploymentControllerService.RejectReleaseUsingPOSTParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  rejectReleaseUsingPOST(
    params: UiDeploymentControllerService.RejectReleaseUsingPOSTParams,
  ): __Observable<DeploymentLog> {
    return this.rejectReleaseUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * getReleaseChanges
   * @param params The `UiDeploymentControllerService.GetReleaseChangesUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getReleaseChangesUsingGETResponse(
    params: UiDeploymentControllerService.GetReleaseChangesUsingGETParams,
  ): __Observable<__StrictHttpResponse<ReleaseChanges>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/release-changes`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ReleaseChanges>;
      }),
    );
  }
  /**
   * getReleaseChanges
   * @param params The `UiDeploymentControllerService.GetReleaseChangesUsingGETParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  getReleaseChangesUsingGET(
    params: UiDeploymentControllerService.GetReleaseChangesUsingGETParams,
  ): __Observable<ReleaseChanges> {
    return this.getReleaseChangesUsingGETResponse(params).pipe(
      __map((_r) => _r.body as ReleaseChanges),
    );
  }

  /**
   * signOffDeployment
   * @param params The `UiDeploymentControllerService.SignOffDeploymentUsingPUTParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  signOffDeploymentUsingPUTResponse(
    params: UiDeploymentControllerService.SignOffDeploymentUsingPUTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/signoff`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * signOffDeployment
   * @param params The `UiDeploymentControllerService.SignOffDeploymentUsingPUTParams` containing the following parameters:
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  signOffDeploymentUsingPUT(
    params: UiDeploymentControllerService.SignOffDeploymentUsingPUTParams,
  ): __Observable<DeploymentLog> {
    return this.signOffDeploymentUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }

  /**
   * triggerRollbackPlanRelease
   * @param params The `UiDeploymentControllerService.TriggerRollbackPlanReleaseUsingPOSTParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  triggerRollbackPlanReleaseUsingPOSTResponse(
    params: UiDeploymentControllerService.TriggerRollbackPlanReleaseUsingPOSTParams,
  ): __Observable<__StrictHttpResponse<DeploymentLog>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}/deployments/${encodeURIComponent(
          String(params.deploymentId),
        )}/${encodeURIComponent(
          String(params.resourceType),
        )}/${encodeURIComponent(String(params.resourceName))}/rollback-plan`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeploymentLog>;
      }),
    );
  }
  /**
   * triggerRollbackPlanRelease
   * @param params The `UiDeploymentControllerService.TriggerRollbackPlanReleaseUsingPOSTParams` containing the following parameters:
   *
   * - `resourceType`: resourceType
   *
   * - `resourceName`: resourceName
   *
   * - `deploymentId`: deploymentId
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  triggerRollbackPlanReleaseUsingPOST(
    params: UiDeploymentControllerService.TriggerRollbackPlanReleaseUsingPOSTParams,
  ): __Observable<DeploymentLog> {
    return this.triggerRollbackPlanReleaseUsingPOSTResponse(params).pipe(
      __map((_r) => _r.body as DeploymentLog),
    );
  }
}

module UiDeploymentControllerService {
  /**
   * Parameters for createDeploymentUsingPOST
   */
  export interface CreateDeploymentUsingPOSTParams {
    /**
     * deploymentRequest
     */
    deploymentRequest: DeploymentRequest;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getLatestReleaseByApplicationUsingGET
   */
  export interface GetLatestReleaseByApplicationUsingGETParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * application
     */
    application: string;
  }

  /**
   * Parameters for abortAutomationSuiteUsingDELETE1
   */
  export interface AbortAutomationSuiteUsingDELETE1Params {
    /**
     * executionId
     */
    executionId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for runHotfixDeploymentRecipeUsingPOST
   */
  export interface RunHotfixDeploymentRecipeUsingPOSTParams {
    /**
     * deploymentRecipe
     */
    deploymentRecipe: HotfixDeploymentRecipe;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * withRefresh
     */
    withRefresh?: boolean;

    /**
     * isPlan
     */
    isPlan?: boolean;

    /**
     * forceRelease
     */
    forceRelease?: boolean;

    /**
     * comment
     */
    comment?: string;

    /**
     * canQueue
     */
    canQueue?: boolean;

    /**
     * allowDestroy
     */
    allowDestroy?: boolean;
  }

  /**
   * Parameters for releaseUsingPUT
   */
  export interface ReleaseUsingPUTParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * withRefresh
     */
    withRefresh?: boolean;

    /**
     * forceRelease
     */
    forceRelease?: boolean;

    /**
     * comment
     */
    comment?: string;

    /**
     * canQueue
     */
    canQueue?: boolean;

    /**
     * allowDestroy
     */
    allowDestroy?: boolean;
  }

  /**
   * Parameters for releaseV2UsingPUT
   */
  export interface ReleaseV2UsingPUTParams {
    /**
     * releaseType
     */
    releaseType: string;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * withRefresh
     */
    withRefresh?: boolean;

    /**
     * forceRelease
     */
    forceRelease?: boolean;

    /**
     * comment
     */
    comment?: string;

    /**
     * canQueue
     */
    canQueue?: boolean;

    /**
     * allowDestroy
     */
    allowDestroy?: boolean;
  }

  /**
   * Parameters for searchDeploymentsUsingGET
   */
  export interface SearchDeploymentsUsingGETParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * triggeredBy
     */
    triggeredBy?: string;

    /**
     * tfVersion
     */
    tfVersion?: string;

    /**
     * status
     */
    status?:
      | 'SUCCEEDED'
      | 'FAILED'
      | 'FAULT'
      | 'TIMED_OUT'
      | 'IN_PROGRESS'
      | 'STOPPED'
      | 'INVALID'
      | 'STARTED'
      | 'UNKNOWN'
      | 'QUEUED'
      | 'PENDING_APPROVAL'
      | 'APPROVED'
      | 'REJECTED';

    /**
     * start
     */
    start?: string;

    /**
     * signedOff
     */
    signedOff?: boolean;

    /**
     * releaseType
     */
    releaseType?:
      | 'HOTFIX'
      | 'RELEASE'
      | 'LAUNCH'
      | 'DESTROY'
      | 'CUSTOM'
      | 'UNLOCK_STATE'
      | 'PLAN'
      | 'HOTFIX_PLAN'
      | 'APPLY_PLAN'
      | 'APPLY_HOTFIX_PLAN'
      | 'SCALE_UP'
      | 'SCALE_DOWN'
      | 'MAINTENANCE'
      | 'TERRAFORM_EXPORT'
      | 'ROLLBACK_PLAN'
      | 'APPLY_ROLLBACK_PLAN';

    /**
     * pageSize
     */
    pageSize?: number;

    /**
     * pageNumber
     */
    pageNumber?: number;

    /**
     * excludeStatus
     */
    excludeStatus?: Array<
      | 'SUCCEEDED'
      | 'FAILED'
      | 'FAULT'
      | 'TIMED_OUT'
      | 'IN_PROGRESS'
      | 'STOPPED'
      | 'INVALID'
      | 'STARTED'
      | 'UNKNOWN'
      | 'QUEUED'
      | 'PENDING_APPROVAL'
      | 'APPROVED'
      | 'REJECTED'
    >;

    /**
     * end
     */
    end?: string;
  }

  /**
   * Parameters for getDeploymentStatsUsingGET
   */
  export interface GetDeploymentStatsUsingGETParams {
    /**
     * clusterId
     */
    clusterId: string;

    /**
     * days
     */
    days?: number;
  }

  /**
   * Parameters for stateUnlockUsingPUT
   */
  export interface StateUnlockUsingPUTParams {
    /**
     * lockId
     */
    lockId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getDeploymentUsingGET
   */
  export interface GetDeploymentUsingGETParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for approveReleaseUsingPOST
   */
  export interface ApproveReleaseUsingPOSTParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for downloadTerraformExportUsingGET
   */
  export interface DownloadTerraformExportUsingGETParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getDeploymentLogsUsingGET
   */
  export interface GetDeploymentLogsUsingGETParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;

    /**
     * nextToken
     */
    nextToken?: string;
  }

  /**
   * Parameters for streamDeploymentLogsUsingGET
   */
  export interface StreamDeploymentLogsUsingGETParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for rejectReleaseUsingPOST
   */
  export interface RejectReleaseUsingPOSTParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for getReleaseChangesUsingGET
   */
  export interface GetReleaseChangesUsingGETParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for signOffDeploymentUsingPUT
   */
  export interface SignOffDeploymentUsingPUTParams {
    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for triggerRollbackPlanReleaseUsingPOST
   */
  export interface TriggerRollbackPlanReleaseUsingPOSTParams {
    /**
     * resourceType
     */
    resourceType: string;

    /**
     * resourceName
     */
    resourceName: string;

    /**
     * deploymentId
     */
    deploymentId: string;

    /**
     * clusterId
     */
    clusterId: string;
  }
}

export { UiDeploymentControllerService };
